import { Link } from "react-router-dom";
import dayjs from "dayjs";
import type { TasksByProjectRow } from "../../../queries/reports";
import type { ProjectProps, SessionUser } from "../../../types/interfaces";
import { routes } from "../../../navigation/routes/routes";
import { ProjectStatusTag } from "../../ui/StatusTag";
import { DashCard } from "./DashCard";
import { PriorityTag, SeverityTag } from "./Tags";
import { formatDate } from "./format";
import styles from "./dash.module.css";

interface Props {
  rows?: TasksByProjectRow[];
  projects?: ProjectProps[];
  users?: SessionUser[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

export const ProjectsTable = ({ rows, projects, users, loading, error, onRetry }: Props) => {
  const projectById = new Map((projects || []).map((p) => [p._id, p]));
  const userName = new Map((users || []).map((u) => [u._id, u.name]));
  return (
    <DashCard
      title="Projects"
      helper="Every project with its severity, priority, team and how much work is open."
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={!rows || rows.length === 0}
      emptyTitle="No projects yet"
      emptyText="Create a project and it will show up here."
    >
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Project</th>
              <th scope="col">Status</th>
              <th scope="col">Severity</th>
              <th scope="col">Priority</th>
              <th scope="col">Lead</th>
              <th scope="col" className={styles.num}>People</th>
              <th scope="col" className={styles.num}>Open tasks</th>
              <th scope="col" className={styles.num}>Backlog</th>
              <th scope="col">Target</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => {
              const p = projectById.get(r.projectId);
              const open = r.total - (r.byStatus.done || 0);
              const target = p?.targetDate;
              const late = !!target && r.status === "active" && dayjs(target).isBefore(dayjs(), "day");
              return (
                <tr key={r.projectId}>
                  <th scope="row" className={styles.rowHead}>
                    <Link className={styles.link} to={routes.projectBoard.replace(":id", r.projectId)}>{r.name}</Link>
                  </th>
                  <td><ProjectStatusTag status={r.status} /></td>
                  <td><SeverityTag severity={r.severity} /></td>
                  <td><PriorityTag priority={r.priority} /></td>
                  <td>{p?.leadId ? userName.get(p.leadId) || "—" : "—"}</td>
                  <td className={`${styles.num} osi-num`}>{p ? p.assignedUserIds.length : "—"}</td>
                  <td className={`${styles.num} osi-num`}>{open}</td>
                  <td className={`${styles.num} osi-num`}>{r.backlog}</td>
                  <td className="osi-num">
                    {formatDate(target)}
                    {late && <span className={styles.lateTag}> Past due</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </DashCard>
  );
};
